import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Rocket } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

function WaitlistForm({ categorySlug }) {
    const [email, setEmail] = useState("");
    const [done, setDone] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!/.+@.+\..+/.test(email)) {
            toast.error("Please enter a valid email.");
            return;
        }
        setSubmitting(true);
        try {
            await api.joinWaitlist({ email, category_slug: categorySlug });
            setDone(true);
            toast.success("You're on the waitlist! We'll email you on launch.");
        } catch {
            toast.error("Something went wrong. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div
                className="text-sm text-indiagreen-700 inline-flex items-center gap-1.5 font-semibold"
                data-testid={`waitlist-done-${categorySlug}`}
            >
                <CheckCircle2 className="h-4 w-4" /> Subscribed — we'll notify
                you on launch
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            className="flex flex-col sm:flex-row gap-2 max-w-md"
            data-testid={`waitlist-form-${categorySlug}`}
        >
            <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@startup.in"
                className="flex-1 bg-white border border-line rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-saffron/30 focus:border-saffron/40"
                data-testid={`waitlist-email-${categorySlug}`}
            />
            <button
                type="submit"
                disabled={submitting}
                className="btn-saffron rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
                data-testid={`waitlist-submit-${categorySlug}`}
            >
                {submitting ? "..." : "Notify me"}
            </button>
        </form>
    );
}

export default function Opportunities() {
    const [opportunities, setOpportunities] = useState([]);

    useEffect(() => {
        document.title = "Open Opportunities — Build for Bharat | BharatStack";
        api.getOpportunities().then(setOpportunities);
    }, []);

    return (
        <div
            className="max-w-5xl mx-auto px-4 lg:px-6 py-12"
            data-testid="opportunities-page"
        >
            <header className="text-center max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-saffron-700 bg-saffron-50 border border-saffron-200 rounded-full px-3 py-1">
                    <Rocket className="h-3.5 w-3.5" /> For Indian founders
                </div>
                <h1 className="mt-5 font-heading font-extrabold text-4xl sm:text-5xl tracking-tight text-ink">
                    🚀 Open Opportunities — Build for Bharat
                </h1>
                <p className="mt-4 text-lg text-ink-muted leading-relaxed">
                    These categories have no Indian alternative. The ecosystem
                    is waiting for you.
                </p>
            </header>

            <div className="mt-12 space-y-10">
                {opportunities.map((opp) => {
                    const o = opp.opportunity || {};
                    return (
                        <section
                            key={opp.slug}
                            className="bg-gradient-to-br from-saffron-50 to-white border border-saffron/30 rounded-3xl overflow-hidden"
                            data-testid={`opp-section-${opp.slug}`}
                        >
                            <div className="p-6 sm:p-10">
                                <div className="flex items-start gap-5">
                                    <div className="text-5xl shrink-0">
                                        {o.icon_emoji || "🚀"}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-ink tracking-tight">
                                            {opp.name}
                                        </h2>
                                        <p className="mt-1 text-base text-ink-muted">
                                            {o.heading}
                                        </p>
                                        <p className="mt-3 font-heading font-bold text-saffron-700 text-lg">
                                            {o.subheading}
                                        </p>
                                    </div>
                                </div>

                                <p className="mt-6 text-ink leading-relaxed text-base">
                                    {o.body}
                                </p>

                                {o.bullets && (
                                    <ul className="mt-5 space-y-2">
                                        {o.bullets.map((b, i) => (
                                            <li
                                                key={i}
                                                className="flex items-start gap-2 text-sm text-ink-muted"
                                            >
                                                <span className="text-saffron mt-0.5">
                                                    •
                                                </span>
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                <div className="mt-6 flex flex-wrap items-center gap-3">
                                    <Link
                                        to={`/submit?category=${opp.slug}`}
                                        className="inline-flex items-center gap-1.5 bg-indiagreen text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-indiagreen-700 transition-colors"
                                        data-testid={`opp-build-${opp.slug}`}
                                    >
                                        I'm Building This
                                        <ArrowRight className="h-4 w-4" />
                                    </Link>
                                    <Link
                                        to={`/category/${opp.slug}`}
                                        className="text-sm font-semibold text-saffron-700 hover:underline"
                                    >
                                        Read the full case →
                                    </Link>
                                </div>
                            </div>

                            <div className="px-6 sm:px-10 py-5 bg-white/60 border-t border-saffron/20">
                                <p className="text-sm font-semibold text-ink mb-2">
                                    Get notified when an Indian {opp.name} launches:
                                </p>
                                <WaitlistForm categorySlug={opp.slug} />
                            </div>
                        </section>
                    );
                })}
            </div>

            <section className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-line rounded-2xl p-6">
                    <h3 className="font-heading font-bold text-ink text-lg">
                        Already building one?
                    </h3>
                    <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                        Get listed on BharatStack before you launch — connect
                        with Indian customers, founders and CIOs early.
                    </p>
                    <Link
                        to="/submit"
                        className="mt-4 inline-flex btn-saffron rounded-lg px-4 py-2 text-sm font-semibold"
                        data-testid="opp-submit-link"
                    >
                        Submit your product →
                    </Link>
                </div>

                <div className="bg-white border border-line rounded-2xl p-6">
                    <h3 className="font-heading font-bold text-ink text-lg">
                        Stay in the loop
                    </h3>
                    <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                        Bookmark BharatStack — new Indian alternatives are
                        added every week. Or follow our build-in-public
                        updates.
                    </p>
                </div>
            </section>
        </div>
    );
}
